import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Theme, Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { Box, TextField, Typography } from 'web-library';
import { WithdrawalReason } from '../../api/candidate/exam/model';
import {
  ErrorObject,
  FormIds,
} from '../accessArrangment/components/candidate/SelectCondition/components/SelectConditionContent';

interface WithdrawalReasonAdditionalTextProps {
  selectedArr: WithdrawalReason;
  textArea?: string;
  setTextArea: (textArea: string) => void;
  displayErrors: boolean;
}

const maxCharacterCount = 200;

const WithdrawalReasonAdditionalText = ({
  selectedArr,
  textArea = '',
  setTextArea,
  displayErrors = false,
}: WithdrawalReasonAdditionalTextProps) => {
  const { t } = useTranslation();
  const [textFieldFormErrors, setTextFieldFormErrors] = useState<ErrorObject[]>([]);

  useEffect(() => {
    const textFieldErrors: ErrorObject[] = [];
    const charactersRemaining = maxCharacterCount - textArea.length;
    if (charactersRemaining < 0) {
      textFieldErrors.push({
        id: FormIds.OTHERS_TEXT_AREA_OVER_LIMIT,
        text:
          t('accessArrangement->>SelectCondition.OthersStringExccedError1') +
          /* Turn negative numbber to positive */
          (0 - charactersRemaining) +
          t('accessArrangement->>SelectCondition.OthersStringExccedError2'),
      });
    }
    setTextFieldFormErrors(textFieldErrors);
  }, [textArea, t]);

  const onHandleTextArea = (e: any) => {
    setTextArea(e.target.value);
  };

  const findError = (errorId: string, textformErrors: ErrorObject[]) =>
    textformErrors.find((textformError) => textformError.id === errorId);

  const overLimitError = findError(FormIds.OTHERS_TEXT_AREA_OVER_LIMIT, textFieldFormErrors)?.text;
  const hasError = (displayErrors && !textArea.length) || !!overLimitError;

  const generateHelperText = () => {
    if (overLimitError) {
      return overLimitError;
    }
    return (
      (maxCharacterCount - textArea.length).toString() +
      t('accessArrangement->>SelectCondition.CharactersRemaining')
    );
  };

  const useStyles = makeStyles()((theme) =>
  ({
    inputLabel: {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(1),
    },
    error: {
      color: theme.color.state.stateError,
      fontSize: '0.875rem',
    },
    inputLabelText: {
      color: theme.color.text.textHeader,
    },
    errorLabelText: {
      color: theme.color.state.stateError,
    },
    errorTextField:{
      '& .MuiInputBase-root': {
        backgroundColor: theme.color.state.stateErrorLight,
        borderColor: theme.color.state.stateError,
        '&:hover': {
          backgroundColor: theme.color.state.stateErrorLight,
          borderColor: theme.color.state.stateError
        },
      },
    },
    textField: {
      '& .MuiInputBase-root': {
        backgroundColor: theme.color.background.bgField,
        borderColor: 'none',
        '&:hover': {
          backgroundColor: theme.color.background.bgField,
          borderColor: 'none',
        },
      },
    },
  }));

  const { classes } = useStyles();

  return (
    <>
      {selectedArr.additionalTextNotice && (
        <>
          <Box className={classes.inputLabel}>
            <Typography
              className={hasError ? classes.errorLabelText : classes.inputLabelText}
              variant="h6"
            >
              {selectedArr.additionalTextNotice === 'Please Specify reason(s)'
                ? 'Please Specify Reason(s)'
                : selectedArr.additionalTextNotice}
            </Typography>
          </Box>
          <TextField
            name="inputReason-withdrawalReason"
            className={hasError ? classes.errorTextField : classes.textField}
            id="text-field-reason"
            multiline
            placeholder={t('withdrawal->>WithdrawalReason.InputReasonsHere')}
            helperText={
              <span className={hasError ? classes.error : ''}>{generateHelperText()}</span>
            }
            rows={5}
            onChange={onHandleTextArea}
            value={textArea}
          />
        </>
      )}
    </>
  );
};

export default WithdrawalReasonAdditionalText;
